import { EarlyWarningTypes } from "../components/createWarningComp/CreateWarningTypes";
import { ResponseTypes } from "./APIService";

const BASE_URL = process.env.REACT_APP_BASE_URL

export const getToken = (): string => {
    const token = localStorage.getItem("access_token");
    if (!token) {
        throw new Error("Missing access token");
    }
    return token
}

// New template
export type APIRequestNewTemplate = {
    id: string;
    type: string;
    warningName: string;
    name: string;
    description: string;
    severity: string;
    preRequestDocument: string;
    primaryCause: string;
    lightning: string;
    messageEn: string;
} 

export const APIAddNewTemplate = async (request: APIRequestNewTemplate): Promise<{ status: boolean; message: string }> => {
    const url = `${BASE_URL}/ew/api/v1/new_template`
    const header = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(request)
    }
  
    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json()
    
        if (!response.ok) 
        {
            // API returned error
            return { status: false, message: responseData.message || "Request failed" }
        }
    
        return { status: true, message: "Template added successfully" }
    } 
    catch (error: any) 
    {
      return { status: false, message: error.message || "Network error" };
    }
}

// Update template
export type APIRequestUpdateTemplate = {
    id: string;
    type: string;
    warningName: string;
    name: string;
    description: string;
    severity: string;
    preRequestDocument: string;
    primaryCause: string;
    lightning: string;
    messageEn: string;
} 

export const APIUpdateTemplate = async (request: APIRequestUpdateTemplate): Promise<{ status: boolean; message: string }> => {
    const url = `${BASE_URL}/ew/api/v1/update_template`
    const header = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(request)
    }
  
    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json()
    
        if (!response.ok) 
        {
            // API returned error
            return { status: false, message: responseData.message || "Request failed" }
        }
    
        return { status: true, message: "Template update successfully" }
    } 
    catch (error: any) 
    {
      return { status: false, message: error.message || "Network error" };
    }
}

export type APIResponseWarningName = {
    warningId: string;
    username: string;
    hazardType: string;
    warningType: string;
    departments: string;
}

export const APIGetWarningNames = async (): Promise<APIResponseWarningName[]> => {
    const url = `${BASE_URL}/workflow_engine/api/v1/getallinformation`
    const header = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        }
    }

    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json()
    
        if (!response.ok) 
        {
            // API returned error
            return []
        }
    
        const data = responseData as ResponseTypes
        return data.data as APIResponseWarningName[]
    } 
    catch (error: any) 
    {
      return []
    }
}

export type APITemplateResponse = {
    id: string;
    type: EarlyWarningTypes;
    warningName: string;
    name: string;
    description: string;
    severity: string;
    preRequestDocument: string;
    primaryCause: string;
    lightning: string;
    messageEn: string;
    username: string;
}

export const APIGetAllTemplates = async (): Promise<{status: boolean, data: APITemplateResponse[]}> => {
    const url = `${BASE_URL}/ew/api/v1/get_all_templates`
    const header = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        }
    }

    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json() as ResponseTypes
        
        if (!response.ok) 
        {
            // API returned error
            return {status: false, data: []}
        }
    
        return {status: true, data: responseData.data}
    } 
    catch (error: any) 
    {
        console.log("ERROR: ", error)
      return {status: false, data: []}
    }
}

export const APIDeleteTemplate = async (id: string): Promise<{status: boolean, message: string}> => {

    const params = new URLSearchParams({id: id})
    const url = `${BASE_URL}/ew/api/v1/delete_template?${params.toString()}`
    const header = {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        }
    }

    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json() as ResponseTypes
        
        if (!response.ok) return {status: false, message: responseData.message}
    
        return {status: true, message: responseData.message}
    } 
    catch (error: any) 
    {
        console.log("ERROR: ", error)
        return {status: false, message: "something is wrong"}
    }
}

export const APIGetTemplate = async (id: string): Promise<{status: boolean, data: APITemplateResponse | null}> => {

    const params = new URLSearchParams({id: id})
    const url = `${BASE_URL}/ew/api/v1/get_templates?${params.toString()}`
    const header = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        }
    }

    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json() as ResponseTypes
        
        if (!response.ok) return {status: false, data: null}
        
        return {status: true, data: responseData.data}
    } 
    catch (error: any) 
    {
        console.log("ERROR: ", error)
        return {status: false, data: null}
    }
}

// Template EW submit
export type APIRequestSubmitEwTemplate = {
    type: string;
    templateId: string;
    polygon: string;
    startDate: string;
    endDate: string;
    publishDate: string;
    messageEn: string;
}

export const APIEwSubmitTemplate = async (body: APIRequestSubmitEwTemplate): Promise<{status: boolean, message: string}> => {
    const url = `${BASE_URL}/ew/api/v1/template_ew_submit`
    const header = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify(body)
    }

    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json() as ResponseTypes
        
        if (!response.ok) return {status: false, message: responseData.message}
    
        return {status: true, message: responseData.message}
    } 
    catch (error: any) 
    {
        console.log("ERROR: ", error)
        return {status: false, message: "something is wrong"}
    }
}

export const APIGetNewSequence = async (): Promise<{status: boolean, message: string}> => {
    const url = `${BASE_URL}/ew/api/v1/template_next_seq`
    const header = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
        }
    }

    try 
    {
        const response = await fetch(url, header)
        const responseData = await response.json() as ResponseTypes
        
        if (!response.ok) return {status: false, message: responseData.message}
    
        return {status: true, message: responseData.data}
    } 
    catch (error: any) 
    {
        console.log("ERROR: ", error)
        return {status: false, message: "something is wrong"}
    }
}